import {
  ApplicationConfig,
  DOCUMENT,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  RendererFactory2,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { tokenInterceptor } from './Modules/Shared/Interceptors/token.interceptor';
import { loadingInterceptor } from './Modules/Shared/Interceptors/loading.interceptor';
import { errorInterceptor } from './Modules/Shared/Interceptors/error.interceptor';
import { colleagueCompletedInterceptor } from './Modules/Shared/Interceptors/colleague-completed.interceptor';
import { LocalStorageService } from './Services/local-storage.service';
import { Language } from './Types/language.type';
import { RouteReuseStrategy } from '@angular/router';
import { ParamChangeReuseStrategy } from './Modules/Shared/Strategies/param-change-reuse.strategy';

function resolveInitialLanguage(): Language {
  const stored = new LocalStorageService().getItem('preferred_lang') as Language | null;
  return stored === 'en' || stored === 'fr' ? stored : 'en';
}

function initializeLanguage(): Promise<unknown> {
  const translate = inject(TranslateService);
  const root = inject(DOCUMENT).documentElement;
  const renderer = inject(RendererFactory2).createRenderer(null, null);
  translate.onLangChange.subscribe(({ lang }) => renderer.setAttribute(root, 'lang', lang));
  return firstValueFrom(translate.use(resolveInitialLanguage()));
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: ParamChangeReuseStrategy },
    provideAppInitializer(initializeLanguage),

    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        tokenInterceptor,
        loadingInterceptor,
        errorInterceptor,
        colleagueCompletedInterceptor,
      ]),
    ),

    provideTranslateService({
      lang: resolveInitialLanguage(),
      fallbackLang: 'en',
    }),
    provideTranslateHttpLoader({
      prefix: `${environment.api.host}/i18n/`,
      suffix: '',
      useHttpBackend: true,
    }),
  ],
};
